import React, { useState, useEffect } from 'react';
import { CloudinaryImage } from '../../types/Gallery';
import CloudinaryImageBrowser from '../Cloudinary/CloudinaryImageBrowser';
import './ImageSelectionModal.css';

interface ImageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImagesSelected: (images: CloudinaryImage[]) => void;
  existingImages?: CloudinaryImage[];
  maxSelection?: number;
  title?: string;
  allowUpload?: boolean;
  showThumbnails?: boolean;
}

const ImageSelectionModal: React.FC<ImageSelectionModalProps> = ({
  isOpen,
  onClose,
  onImagesSelected,
  existingImages = [],
  maxSelection = 50,
  title = "Select Images",
  allowUpload = true,
  showThumbnails = true
}) => {
  const [selectedImages, setSelectedImages] = useState<CloudinaryImage[]>([]);
  const [showCloudinaryBrowser, setShowCloudinaryBrowser] = useState(false);

  // Reset selections when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedImages([]);
    }
  }, [isOpen]);

  const handleImageSelection = (images: CloudinaryImage[]) => {
    setSelectedImages(images);
    setShowCloudinaryBrowser(false);
  };

  const handleConfirmSelection = () => {
    onImagesSelected(selectedImages);
    onClose();
  };

  const handleRemoveImage = (imageId: string) => {
    setSelectedImages(prev => prev.filter(img => img.publicId !== imageId));
  };

  const handleClearAll = () => {
    setSelectedImages([]);
  };

  if (!isOpen) return null;

  return (
    <div className="image-selection-overlay">
      <div className="image-selection-modal">
        <div className="image-selection-header">
          <h2>{title}</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="image-selection-content">
          {/* Selected Images Preview */}
          {selectedImages.length > 0 && (
            <div className="selected-images-section">
              <div className="selected-images-header">
                <h3>Selected Images ({selectedImages.length})</h3>
                <div className="selected-actions">
                  <button 
                    className="clear-all-btn"
                    onClick={handleClearAll}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              
              <div className="selected-images-grid">
                {selectedImages.map((image, index) => (
                  <div key={image.publicId} className="selected-image-item">
                    <img 
                      src={image.url || image.secure_url} 
                      alt={image.alt || `Selected image ${index + 1}`}
                      className="selected-image-thumbnail"
                    />
                    <button 
                      className="remove-image-btn"
                      onClick={() => handleRemoveImage(image.publicId)}
                      title="Remove image"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                    <div className="image-info">
                      <span className="image-name">
                        {image.filename || image.alt || `Image ${index + 1}`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="image-selection-actions">
            <button 
              className="browse-cloudinary-btn"
              onClick={() => setShowCloudinaryBrowser(true)}
            >
              <i className="fas fa-images"></i>
              Browse Cloudinary Images
            </button>
            
            {allowUpload && (
              <button 
                className="upload-new-btn"
                onClick={() => {
                  // TODO: Implement upload widget
                  console.log('Upload new images');
                }}
              >
                <i className="fas fa-upload"></i>
                Upload New Images
              </button>
            )}
          </div>

          {/* Selection Summary */}
          <div className="selection-summary">
            <span className="selection-count">
              {selectedImages.length} of {maxSelection} images selected
            </span>
            {selectedImages.length > 0 && (
              <span className="selection-size">
                Total: {selectedImages.reduce((acc, img) => acc + (img.width || 0) * (img.height || 0), 0).toLocaleString()} pixels
              </span>
            )}
          </div>
        </div>

        <div className="image-selection-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="confirm-btn"
            onClick={handleConfirmSelection}
            disabled={selectedImages.length === 0}
          >
            Confirm Selection ({selectedImages.length})
          </button>
        </div>
      </div>

      {/* Cloudinary Browser Modal */}
      {showCloudinaryBrowser && (
        <CloudinaryImageBrowser
          onImagesSelected={handleImageSelection}
          onClose={() => setShowCloudinaryBrowser(false)}
          existingImages={existingImages.map(img => img.publicId)}
          maxSelection={maxSelection - selectedImages.length}
          insideModal={false}
          showContinueButton={true}
        />
      )}
    </div>
  );
};

export default ImageSelectionModal;
